import { Box, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Vocabulary } from "../../Utils/Vocabulary";

interface StepActionsProps {
  onNext: () => void | Promise<void | boolean>;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  nextDisabled?: boolean;
  nextLabel?: string;
  previousLabel?: string;
  loading?: boolean;
}

/**
 * Reusable component for step navigation buttons.
 * Provides consistent Previous/Continue buttons across all steps.
 *
 * @param onNext - Handler for continue/next button
 * @param onPrevious - Handler for previous button
 * @param isFirstStep - Whether this is the first step (hides previous button)
 * @param isLastStep - Whether this is the last step (changes button text/icon)
 * @param nextDisabled - Whether the next button should be disabled
 * @param nextLabel - Custom label for next button
 * @param previousLabel - Custom label for previous button
 * @param loading - Whether the form is in a loading state
 */
export const StepActions: React.FC<StepActionsProps> = ({
  onNext,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
  nextDisabled = false,
  nextLabel,
  previousLabel,
  loading = false,
}) => {
  const hasPrevious = !isFirstStep && !!onPrevious;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: hasPrevious ? "space-between" : "center",
        mt: { xs: 0, sm: 4 },
        position: { xs: "fixed", sm: "static" },
        bottom: { xs: 0, sm: "auto" },
        left: { xs: 0, sm: "auto" },
        right: { xs: 0, sm: "auto" },
        zIndex: { xs: 1200, sm: "auto" },
        bgcolor: { xs: "background.paper", sm: "transparent" },
        px: { xs: 2, sm: 0 },
        py: { xs: 1.5, sm: 0 },
        boxShadow: { xs: "0 -2px 12px rgba(0,0,0,0.10)", sm: "none" },
      }}
    >
      {hasPrevious && (
        <Button
          variant="outlined"
          onClick={onPrevious}
          startIcon={<ArrowBackIcon />}
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#FF6633",
            color: "#FF6633",
            "&:hover": {
              borderColor: "#e55a2b",
              bgcolor: "rgba(255, 102, 51, 0.04)",
            },
            "&:disabled": {
              borderColor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          {previousLabel || Vocabulary.previous}
        </Button>
      )}

      <Button
        variant="contained"
        onClick={onNext}
        disabled={nextDisabled || loading}
        endIcon={isLastStep ? <CheckCircleIcon /> : <ArrowForwardIcon />}
        sx={{
          borderRadius: 2,
          px: 5,
          py: 1.5,
          textTransform: "none",
          fontWeight: 600,
          bgcolor: "#FF6633",
          color: "white",
          width: hasPrevious ? "auto" : { xs: "100%", sm: "auto" },
          "&:hover": {
            bgcolor: "#e55a2b",
          },
          "&:disabled": {
            bgcolor: "#e0e0e0",
            color: "#9e9e9e",
          },
        }}
      >
        {nextLabel || (isLastStep ? "Finalizeaza" : Vocabulary.continue)}
      </Button>
    </Box>
  );
};
